import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useState } from 'react'

const accent = '#58a6ff'
const green = '#3fb950'
const yellow = '#d29922'
const orange = '#db6d28'
const purple = '#bc8cff'

const VersionBadge = ({ type }: { type: 'major' | 'minor' | 'patch' }) => {
  const colors = { major: orange, minor: yellow, patch: green }
  return (
    <Chip
      label={type.toUpperCase()}
      size="small"
      sx={{
        fontSize: '0.65rem',
        fontWeight: 700,
        height: 20,
        bgcolor: `${colors[type]}22`,
        color: colors[type],
        letterSpacing: 0.5,
      }}
    />
  )
}

const ChainNode = ({
  label,
  version,
  name,
  color,
}: {
  label: string
  version: string
  name: string
  color: string
}) => (
  <Box
    sx={{
      border: `1px solid ${color}`,
      borderRadius: 1.5,
      p: '8px 12px',
      textAlign: 'center',
      minWidth: 100,
      bgcolor: 'background.paper',
    }}
  >
    <Typography sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5, color }}>{label}</Typography>
    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}>{version}</Typography>
    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{name}</Typography>
  </Box>
)

const ToolCard = ({ name, desc }: { name: string; desc: string }) => (
  <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: green, fontWeight: 600 }}>{name}</Typography>
    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>{desc}</Typography>
  </Box>
)

const SectionCard = ({
  badge,
  title,
  description,
  example,
}: {
  badge: 'major' | 'minor' | 'patch'
  title: string
  description: string
  example: string
}) => (
  <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 1 }}>
    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
      <VersionBadge type={badge} />
      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{title}</Typography>
    </Stack>
    <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>{description}</Typography>
    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic' }}>"{example}"</Typography>
  </Box>
)

export const FeatureReferenceButton = () => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()

  return (
    <>
      <Tooltip title="Feature Reference" arrow>
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark' ? '#0d1117' : 'background.paper',
            backgroundImage: 'none',
            maxHeight: '100vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: accent }}>
            Deal Versioning & AI Agent
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Typography
          sx={{ px: 3, fontSize: '0.85rem', color: 'text.secondary', borderBottom: 1, borderColor: 'divider', pb: 1.5 }}
        >
          Internal reference for the Grobotic team
        </Typography>

        <DialogContent sx={{ pt: 2 }}>
          {/* Section 1: Versioning Overview */}
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: accent, mt: 1, mb: 1 }}>
            1. Deal Versioning Overview
          </Typography>
          <Typography sx={{ fontSize: '0.88rem', mb: 1.5 }}>
            Every deal carries a <strong>semantic version</strong> (
            <code
              style={{
                background: 'rgba(110,118,129,0.15)',
                padding: '2px 6px',
                borderRadius: 4,
                fontFamily: 'monospace',
              }}
            >
              X.Y.Z
            </code>
            ) that tracks its full history. Deals in the same agreement family are linked into a{' '}
            <strong>lineage chain</strong>.
          </Typography>

          {/* Version type cards */}
          <Stack direction="row" gap={1} mb={2}>
            {[
              {
                label: 'X.0.0',
                type: 'Major',
                trigger: 'New root agreement (MSA, CSA) or full product restructure',
                color: orange,
              },
              {
                label: 'X.Y.0',
                type: 'Minor',
                trigger: 'Renewals (RMSA/RCSA), date changes, or adding/removing products',
                color: yellow,
              },
              {
                label: 'X.Y.Z',
                type: 'Patch',
                trigger: 'Pricing/budget adjustments on the existing deal (no new contract)',
                color: green,
              },
            ].map(v => (
              <Box
                key={v.type}
                sx={{
                  flex: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1.5,
                  textAlign: 'center',
                  borderTop: `3px solid ${v.color}`,
                }}
              >
                <Typography sx={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 700, color: v.color }}>
                  {v.label}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5 }}>{v.type} Version</Typography>
                <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>{v.trigger}</Typography>
              </Box>
            ))}
          </Stack>

          {/* Agreement families table */}
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>Agreement Families</Typography>
          <Table
            size="small"
            sx={{
              mb: 2,
              '& th': { color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' },
              '& td': { fontSize: '0.85rem' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Family</TableCell>
                <TableCell>Root Type</TableCell>
                <TableCell>Revision Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>MSA</TableCell>
                <TableCell>MSA</TableCell>
                <TableCell>RMSA</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>MSA - Housing</TableCell>
                <TableCell>MSA - Housing</TableCell>
                <TableCell>RMSA - Housing</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>CSA</TableCell>
                <TableCell>CSA</TableCell>
                <TableCell>RCSA</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>IO</TableCell>
                <TableCell colSpan={2}>Standalone - not versioned</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Lineage chain visual */}
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>Deal Lineage Chain</Typography>
          <Typography sx={{ fontSize: '0.85rem', mb: 1 }}>
            Each version links to the next via{' '}
            <code
              style={{
                background: 'rgba(110,118,129,0.15)',
                padding: '2px 6px',
                borderRadius: 4,
                fontFamily: 'monospace',
              }}
            >
              predecessorId
            </code>{' '}
            /{' '}
            <code
              style={{
                background: 'rgba(110,118,129,0.15)',
                padding: '2px 6px',
                borderRadius: 4,
                fontFamily: 'monospace',
              }}
            >
              successorId
            </code>
            , and every deal in the chain shares the same master agreement ID.
          </Typography>

          <Stack direction="row" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <ChainNode label="Master" version="v1.0.0" name="Acme - MSA" color={accent} />
            <Typography sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>&rarr;</Typography>
            <ChainNode label="Revision" version="v1.1.0" name="Acme - RMSA" color={purple} />
            <Typography sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>&rarr;</Typography>
            <ChainNode label="Revision" version="v1.2.0" name="Acme - RMSA" color={purple} />
            <Typography sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>&rarr;</Typography>
            <ChainNode label="Master" version="v2.0.0" name="Acme - MSA" color={accent} />
          </Stack>

          <Box
            sx={{
              bgcolor: `${accent}11`,
              borderLeft: `3px solid ${accent}`,
              p: 1.25,
              borderRadius: '0 6px 6px 0',
              mb: 1.5,
              fontSize: '0.85rem',
            }}
          >
            <strong>Patch versions</strong> (e.g. v1.0.0 &rarr; v1.0.1) don't create new deals. They update the existing
            deal in place and record changes in the <strong>Patch History</strong> log.
          </Box>

          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>Where You'll See It</Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { fontSize: '0.85rem', mb: 0.5 } }}>
            <li>
              <strong>Deal Info Card</strong> - "Deal Lineage" section shows the full chain with clickable nodes.
            </li>
            <li>
              <strong>Versions Tab</strong> - Timeline lists every version with patch history details.
            </li>
            <li>
              <strong>Deals Grid</strong> - Version column displays the current semantic version.
            </li>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Section 2: AI Agent */}
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: accent, mb: 1 }}>
            2. AI Agent (GROBX AI)
          </Typography>
          <Typography sx={{ fontSize: '0.88rem', mb: 1.5 }}>
            The AI agent is available from any deal or organization page. It understands your data, follows versioning
            rules automatically, and can make changes across multiple deals at once.
          </Typography>

          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.75 }}>What It Can Do</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <ToolCard
              name="Search Organizations"
              desc="Find orgs by name or DBA. Returns contacts, verticals, and team info."
            />
            <ToolCard name="Get Organization Deals" desc="Fetch all deals for an org with full product details." />
            <ToolCard
              name="Search Product Catalog"
              desc="Look up available services with default pricing and margins."
            />
            <ToolCard name="Create Deal" desc="Create new MSA, RMSA, CSA, or IO deals with products and versioning." />
            <ToolCard
              name="Apply Patch/Minor Version"
              desc="Update pricing (patch) or dates (minor) on a single deal in place."
            />
            <ToolCard
              name="Find Deals by Product"
              desc="Search all active deals containing a specific product/service."
            />
            <ToolCard
              name="Compute Spend Summary"
              desc="Financial rollups by period - revenue, profit, media spend breakdowns."
            />
          </Box>

          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.75 }}>How It Works with Versioning</Typography>
          <SectionCard
            badge="patch"
            title="Price/Budget Change"
            description="Tell the AI to change a product's price. It updates the deal in place, bumps the patch version, and logs the change in patch history. No new contract needed."
            example="Change Paid Search from $1,600 to $1,800/mo on the Acme MSA"
          />
          <SectionCard
            badge="minor"
            title="Date Renewal or Service Tweak"
            description="Ask the AI to extend dates or make service adjustments. It bumps the minor version. If a new contract is needed, it creates a new deal linked to the predecessor."
            example="Extend all products on this deal through December 2026"
          />
          <SectionCard
            badge="major"
            title="New Agreement or Product Restructure"
            description="For significant changes, the AI creates a brand new deal with a new major version, linked back to the predecessor. This always requires a new PandaDoc contract."
            example="Create a new MSA for Acme adding SEO and Meta Advertising"
          />

          <Box
            sx={{
              bgcolor: `${accent}11`,
              borderLeft: `3px solid ${accent}`,
              p: 1.25,
              borderRadius: '0 6px 6px 0',
              mb: 1.5,
              fontSize: '0.85rem',
            }}
          >
            The AI <strong>always asks for confirmation</strong> before creating or modifying deals. You'll see a
            summary of exactly what will change before anything is written.
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Section 3: Quick Reference */}
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: accent, mb: 1 }}>
            3. Quick Reference
          </Typography>
          <Table
            size="small"
            sx={{
              '& th': { color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' },
              '& td': { fontSize: '0.85rem' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Version Bump</TableCell>
                <TableCell>New Contract?</TableCell>
                <TableCell>New Deal?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Change a product's price</TableCell>
                <TableCell>
                  <VersionBadge type="patch" />
                </TableCell>
                <TableCell>No</TableCell>
                <TableCell>No</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Extend deal/product dates</TableCell>
                <TableCell>
                  <VersionBadge type="minor" />
                </TableCell>
                <TableCell>Optional</TableCell>
                <TableCell>Optional</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Add or remove products</TableCell>
                <TableCell>
                  <VersionBadge type="major" />
                </TableCell>
                <TableCell>Yes</TableCell>
                <TableCell>Yes</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Full renewal (RMSA/RCSA)</TableCell>
                <TableCell>
                  <VersionBadge type="minor" />
                </TableCell>
                <TableCell>Yes</TableCell>
                <TableCell>Yes</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Brand new agreement (MSA)</TableCell>
                <TableCell>
                  <VersionBadge type="major" />
                </TableCell>
                <TableCell>Yes</TableCell>
                <TableCell>Yes</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.78rem', mt: 3 }}>
            Grobotic Internal &mdash; March 2026
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}
